import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { AlertConfirmState, AlertToast, AlertType } from './alert.model';

interface PendingConfirmRequest {
  state: AlertConfirmState;
  resolve: (value: boolean) => void;
}

@Injectable({
  providedIn: 'root'
})
export class AlertService {
  private translate = inject(TranslateService);
  private toastId = 0;
  private confirmId = 0;
  private readonly toastsSubject = new BehaviorSubject<AlertToast[]>([]);
  private readonly confirmSubject = new BehaviorSubject<AlertConfirmState | null>(null);
  private readonly confirmQueue: PendingConfirmRequest[] = [];
  private readonly toastTimers = new Map<number, ReturnType<typeof setTimeout>>();

  readonly toasts$ = this.toastsSubject.asObservable();
  readonly confirm$ = this.confirmSubject.asObservable();

  showSuccess(message: string, title?: string): void {
    this.showAlert('success', title ?? this.translate.instant('ALERTS.SUCCESS_TITLE'), message);
  }

  showError(message: string, title?: string): void {
    this.showAlert('error', title ?? this.translate.instant('ALERTS.ERROR_TITLE'), message, 4200);
  }

  showInfo(message: string, title?: string): void {
    this.showAlert('info', title ?? this.translate.instant('ALERTS.INFO_TITLE'), message, 3200);
  }

  async showConfirm(
    message: string,
    title = 'Confirmación',
    confirmText = 'Aceptar',
    cancelText = 'Cancelar'
  ): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      this.confirmQueue.push({
        state: {
          id: ++this.confirmId,
          type: 'warning',
          title,
          message,
          confirmText,
          cancelText
        },
        resolve
      });

      this.openNextConfirm();
    });
  }

  dismissToast(toastId: number): void {
    const next = this.toastsSubject.value.filter(toast => toast.id !== toastId);
    this.toastsSubject.next(next);
    this.clearToastTimer(toastId);
  }

  confirmCurrent(): void {
    this.resolveCurrentConfirm(true);
  }

  cancelCurrent(): void {
    this.resolveCurrentConfirm(false);
  }

  private showAlert(type: AlertType, title: string, message: string, duration = 3000): void {
    const toast: AlertToast = {
      id: ++this.toastId,
      type,
      title,
      message,
      duration,
      dismissible: true
    };

    this.toastsSubject.next([...this.toastsSubject.value, toast]);
    this.scheduleToastDismiss(toast);
  }

  private scheduleToastDismiss(toast: AlertToast): void {
    this.clearToastTimer(toast.id);
    const timer = setTimeout(() => {
      this.dismissToast(toast.id);
    }, toast.duration);

    this.toastTimers.set(toast.id, timer);
  }

  private clearToastTimer(toastId: number): void {
    const timer = this.toastTimers.get(toastId);
    if (timer) {
      clearTimeout(timer);
      this.toastTimers.delete(toastId);
    }
  }

  private openNextConfirm(): void {
    if (this.confirmSubject.value || !this.confirmQueue.length) {
      return;
    }

    this.confirmSubject.next(this.confirmQueue[0].state);
  }

  private resolveCurrentConfirm(value: boolean): void {
    const current = this.confirmSubject.value;
    if (!current) {
      return;
    }

    const [pending] = this.confirmQueue.splice(0, 1);
    this.confirmSubject.next(null);

    if (pending) {
      pending.resolve(value);
    }

    queueMicrotask(() => {
      if (!this.confirmSubject.value) {
        this.openNextConfirm();
      }
    });
  }
}
